import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useExportDataContext} from '../contexts/export-data/context';
import {
  ExportPreviewFactory,
  ExportPreviewPdfData,
  ExportPreviewXlsxData,
} from '../contexts/export-data/types';
import {useSnackBar} from '../contexts/snack-bar/context';
import {translations} from '../i18n/translations';

export type ExportErrorNotificationChannel = 'office' | 'daily-care-log';

export function useExportDataWithErrorHandler(
  errorNotificationChannel: ExportErrorNotificationChannel = 'office'
) {
  const {t} = useTranslation();
  const snackbar = useSnackBar();
  const {previewPdf, previewXlsx} = useExportDataContext();
  const setErrorNotification =
    errorNotificationChannel === 'daily-care-log'
      ? snackbar.setDailyCareLogToastNotification
      : snackbar.setOfficeToastNotification;

  const previewPdfWithErrorHandling = useCallback(
    async (factory: ExportPreviewFactory<ExportPreviewPdfData>) => {
      try {
        await previewPdf(factory);
      } catch (error) {
        console.error('Error generating PDF:', error);
        setErrorNotification({
          severity: 'error',
          message: t(translations.export.error.pdf_generation),
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewPdf, setErrorNotification, t]
  );

  const previewXlsxWithErrorHandling = useCallback(
    async (factory: ExportPreviewFactory<ExportPreviewXlsxData>) => {
      try {
        await previewXlsx(factory);
      } catch (error) {
        console.error('Error generating XLSX:', error);
        setErrorNotification({
          severity: 'error',
          message: t(translations.export.error.xlsx_generation),
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewXlsx, setErrorNotification, t]
  );

  return {
    previewPdfWithErrorHandling,
    previewXlsxWithErrorHandling,
  };
}
